import { supabase } from '@/integrations/supabase/client';
import logger from '@/utils/logger';
import { networkService } from './network.service';
import { offlineDb } from '@/db/offlineDb';
import { syncService } from './sync.service';
import { createUserProfile } from './auth/profile.service';
import { getCachedUser } from './auth/sessionCache';
import { getValidUserId, isValidUUID } from '@/utils/validation';

export interface TutorialPreferences {
  globalEnabled: boolean;
  seenTutorials: Record<string, boolean>;
}

const OFFLINE_USER_PLACEHOLDER = 'offline-user';

interface RealignOptions {
  fromId?: string | null;
  toId: string;
  prefsOverride?: TutorialPreferences;
  timestamp: number;
  status?: 'pending' | 'synced';
}

const realignLocalTutorialPreferences = async ({
  fromId,
  toId,
  prefsOverride,
  timestamp,
  status
}: RealignOptions) => {
  if (!fromId || fromId === toId) {
    return;
  }

  try {
    const existing = await offlineDb.tutorialPreferences.get(fromId);
    const preferences =
      prefsOverride || (existing?.preferences as TutorialPreferences | undefined);

    if (existing) {
      await offlineDb.tutorialPreferences.delete(fromId);
    }

    if (preferences) {
      await offlineDb.tutorialPreferences.put({
        user_id: toId,
        preferences,
        _syncStatus: status ?? (existing?._syncStatus ?? 'pending'),
        _lastModified: timestamp
      });
    }

    const queueItems = await offlineDb.syncQueue
      .where('entityType')
      .equals('tutorialPreferences')
      .and(item => item.entityId === fromId)
      .toArray();

    await Promise.all(
      queueItems.map(item =>
        offlineDb.syncQueue.update(item.id!, {
          entityId: toId,
          data: {
            ...(item.data ?? {}),
            id: toId,
            tutorial_preferences:
              prefsOverride ??
              item.data?.tutorial_preferences ??
              preferences
          },
          status: 'pending'
        })
      )
    );
  } catch (alignmentError) {
    logger.logError(
      'tutorialPreferences',
      'Failed to realign tutorial preference identifiers',
      { additionalInfo: alignmentError }
    );
  }
};

const getLocalPreferences = async (userId: string): Promise<TutorialPreferences | null> => {
  const local = await offlineDb.tutorialPreferences.get(userId);
  return local ? (local.preferences as TutorialPreferences) : null;
};

export const getTutorialPreferences = async (
  userId: string
): Promise<TutorialPreferences | null> => {
  const storedUserId = getValidUserId();
  const effectiveUserId = isValidUUID(userId) ? userId : storedUserId;
  const offlineLookupId = effectiveUserId ?? OFFLINE_USER_PLACEHOLDER;

  const getOfflineFallback = async () => {
    const direct = await getLocalPreferences(offlineLookupId);
    if (direct) {
      return direct;
    }

    if (offlineLookupId !== OFFLINE_USER_PLACEHOLDER) {
      const fallback = await getLocalPreferences(OFFLINE_USER_PLACEHOLDER);
      if (fallback) {
        return fallback;
      }
    }

    return null;
  };

  try {
    if (!networkService.isOnline() || !effectiveUserId) {
      return await getOfflineFallback();
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('tutorial_preferences')
      .eq('id', effectiveUserId)
      .single();

    if (error) {
      logger.logError('tutorialPreferences','Error loading preferences',{additionalInfo:error});
      return await getOfflineFallback();
    }

    const prefs = (data?.tutorial_preferences as TutorialPreferences) || null;

    if (prefs) {
      const timestamp = Date.now();
      await realignLocalTutorialPreferences({
        fromId: OFFLINE_USER_PLACEHOLDER,
        toId: effectiveUserId,
        prefsOverride: prefs,
        timestamp,
        status: 'synced'
      });
      await offlineDb.tutorialPreferences.put({
        user_id: effectiveUserId,
        preferences: prefs,
        _syncStatus: 'synced',
        _lastModified: timestamp
      });
    }

    return prefs;
  } catch (error) {
    logger.logError('tutorialPreferences','Failed to load preferences',{additionalInfo:error});
    return await getOfflineFallback();
  }
};

export const saveTutorialPreferences = async (
  userId: string,
  prefs: TutorialPreferences
): Promise<boolean> => {
  const timestamp = Date.now();
  const storedUserId = getValidUserId();
  const effectiveUserId = isValidUUID(userId) ? userId : storedUserId;
  const targetUserId = effectiveUserId ?? OFFLINE_USER_PLACEHOLDER;

  const queueOfflineUpdate = async (
    targetId: string,
    realignFrom?: string | null
  ) => {
    if (realignFrom) {
      await realignLocalTutorialPreferences({
        fromId: realignFrom,
        toId: targetId,
        prefsOverride: prefs,
        timestamp,
        status: 'pending'
      });
    }

    if (targetId !== OFFLINE_USER_PLACEHOLDER) {
      await realignLocalTutorialPreferences({
        fromId: OFFLINE_USER_PLACEHOLDER,
        toId: targetId,
        prefsOverride: prefs,
        timestamp,
        status: 'pending'
      });
    }

    await offlineDb.tutorialPreferences.put({
      user_id: targetId,
      preferences: prefs,
      _syncStatus: 'pending',
      _lastModified: timestamp
    });

    await syncService.addToSyncQueue(
      'tutorialPreferences',
      'update',
      targetId,
      { id: targetId, tutorial_preferences: prefs }
    );
  };

  const persistSynced = async (targetId: string) => {
    await offlineDb.tutorialPreferences.put({
      user_id: targetId,
      preferences: prefs,
      _syncStatus: 'synced',
      _lastModified: timestamp
    });
  };

  try {
    if (!networkService.isOnline()) {
      await queueOfflineUpdate(
        targetUserId,
        userId !== targetUserId ? userId : undefined
      );
      return true;
    }

    if (!effectiveUserId) {
      await queueOfflineUpdate(
        targetUserId,
        userId !== targetUserId ? userId : undefined
      );
      return false;
    }

    if (userId !== effectiveUserId) {
      await realignLocalTutorialPreferences({
        fromId: userId,
        toId: effectiveUserId,
        prefsOverride: prefs,
        timestamp,
        status: 'pending'
      });
    }

    await realignLocalTutorialPreferences({
      fromId: OFFLINE_USER_PLACEHOLDER,
      toId: effectiveUserId,
      prefsOverride: prefs,
      timestamp,
      status: 'pending'
    });

    let { error } = await supabase
      .from('profiles')
      .upsert(
        { id: effectiveUserId, tutorial_preferences: prefs },
        { onConflict: 'id' }
      );

    if (error && error.code === '23503') {
      const user = await getCachedUser();
      const name =
        user?.user_metadata?.name ||
        user?.email?.split('@')[0] ||
        'User';
      const email = user?.email || '';

      const profileCreated = await createUserProfile(
        effectiveUserId,
        name,
        email
      );

      if (!profileCreated) {
        await queueOfflineUpdate(OFFLINE_USER_PLACEHOLDER, effectiveUserId);
        return false;
      }

      const { data: profileData, error: profileLookupError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', effectiveUserId)
        .maybeSingle();

      if (profileLookupError || !profileData) {
        await queueOfflineUpdate(OFFLINE_USER_PLACEHOLDER, effectiveUserId);
        return false;
      }

      ({ error } = await supabase
        .from('profiles')
        .upsert(
          { id: effectiveUserId, tutorial_preferences: prefs },
          { onConflict: 'id' }
        ));

      if (error && error.code === '23503') {
        await queueOfflineUpdate(OFFLINE_USER_PLACEHOLDER, effectiveUserId);
        return false;
      }
    }

    if (error) {
      logger.logError('tutorialPreferences','Error saving preferences',{additionalInfo:error});
      await queueOfflineUpdate(effectiveUserId);
      return false;
    }

    await persistSynced(effectiveUserId);

    return true;
  } catch (error) {
    logger.logError('tutorialPreferences','Failed to save preferences',{additionalInfo:error});
    await queueOfflineUpdate(
      targetUserId,
      userId !== targetUserId ? userId : undefined
    );
    return false;
  }
};
